import { useState } from 'react'

import type { LumpsumPlanRecommendationStatusReason } from '../../api/recommendation.types'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'

type PlanRecommendationStatusReasonBannerSectionProps = {
  planDetails: (LumpsumPlanRecommendationStatusReason & {
    fundName?: string | null
    isin?: string | null
  }) | null
  analyticsScreen?: string
}

export function PlanRecommendationStatusReasonBannerSection({
  planDetails,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: PlanRecommendationStatusReasonBannerSectionProps) {
  const header = planDetails?.recommendationStatusReasonHeader
  const reason = planDetails?.recommendationStatusReason
  if (!header || !reason) {
    return null
  }

  return (
    <div className="px-5 pb-4">
      <PlanRecommendationStatusReasonBanner
        header={header}
        reason={reason}
        analyticsScreen={analyticsScreen}
        fundName={planDetails?.fundName ?? ''}
        fundIsin={planDetails?.isin ?? ''}
      />
    </div>
  )
}

type PlanRecommendationStatusReasonBannerProps = {
  header: string
  reason: string
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}

export function PlanRecommendationStatusReasonBanner({
  header,
  reason,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation,
  fundName = '',
  fundIsin = ''
}: PlanRecommendationStatusReasonBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false)

  if (isDismissed) {
    return null
  }

  return (
    <div className="relative flex items-center gap-3 rounded-[12px] bg-[#E4FCEB] p-4">
      <div className="w-[3px] shrink-0 self-stretch rounded-[10px] bg-[#00661D]" aria-hidden />
      <div className="min-w-0 flex-1 pr-5">
        <div className="text-[13px] font-[700] leading-[150%] text-[#00661D]">{header}</div>
        <div className="text-[13px] font-[400] leading-[150%] text-[#00661D]">{reason}</div>
      </div>
      <div
        className="absolute right-3 top-3 cursor-pointer"
        onClick={() => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              ScreenName: analyticsScreen,
              eventId: PlansLumpsumSheetEventIds.BannerDismiss,
              buttonLabel: 'Dismiss',
              ...(fundName ? { fundName } : {}),
              ...(fundIsin ? { fundIsin } : {})
            }
          })
          setIsDismissed(true)
        }}
      >
        <PlanRecommendationStatusReasonBannerCloseIcon />
      </div>
    </div>
  )
}

export function PlanRecommendationStatusReasonBannerCloseIcon() {
  return (
    <div className="relative scale-105">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8Z"
          fill="white"
        />
      </svg>
      <div className="absolute left-1 top-1 z-[1]">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden>
          <path
            d="M4.84839 4.21289C4.82046 4.18503 4.79829 4.15192 4.78317 4.11547C4.76804 4.07903 4.76026 4.03995 4.76026 4.00049C4.76026 3.96103 4.76804 3.92196 4.78317 3.88551C4.79829 3.84907 4.82046 3.81596 4.84839 3.78809L6.86799 1.76849C6.94748 1.68319 6.99076 1.57036 6.9887 1.45378C6.98664 1.3372 6.93942 1.22597 6.85697 1.14352C6.77452 1.06107 6.66329 1.01385 6.54671 1.01179C6.43013 1.00973 6.3173 1.05301 6.23199 1.13249L4.21239 3.15209C4.18453 3.18003 4.15142 3.2022 4.11497 3.21732C4.07853 3.23245 4.03945 3.24023 3.99999 3.24023C3.96053 3.24023 3.92146 3.23245 3.88501 3.21732C3.84857 3.2022 3.81546 3.18003 3.78759 3.15209L1.76799 1.13249C1.68269 1.05301 1.56986 1.00973 1.45328 1.01179C1.3367 1.01385 1.22547 1.06107 1.14302 1.14352C1.06057 1.22597 1.01335 1.3372 1.01129 1.45378C1.00923 1.57036 1.05251 1.68319 1.13199 1.76849L3.15159 3.78809C3.17953 3.81596 3.2017 3.84907 3.21682 3.88551C3.23195 3.92196 3.23973 3.96103 3.23973 4.00049C3.23973 4.03995 3.23195 4.07903 3.21682 4.11547C3.2017 4.15192 3.17953 4.18503 3.15159 4.21289L1.13199 6.23249C1.08778 6.27369 1.05232 6.32337 1.02773 6.37857C1.00313 6.43377 0.989906 6.49336 0.98884 6.55378C0.987773 6.6142 0.998888 6.67422 1.02152 6.73025C1.04415 6.78629 1.07784 6.83719 1.12057 6.87992C1.1633 6.92265 1.2142 6.95634 1.27024 6.97897C1.32627 7.0016 1.38629 7.01272 1.44671 7.01165C1.50713 7.01058 1.56672 6.99736 1.62192 6.97276C1.67712 6.94817 1.7268 6.91271 1.76799 6.86849L3.78759 4.84889C3.81546 4.82096 3.84857 4.79879 3.88501 4.78367C3.92146 4.76854 3.96053 4.76076 3.99999 4.76076C4.03945 4.76076 4.07853 4.76854 4.11497 4.78367C4.15142 4.79879 4.18453 4.82096 4.21239 4.84889L6.23199 6.86849C6.3173 6.94798 6.43013 6.99126 6.54671 6.9892C6.66329 6.98714 6.77452 6.93991 6.85697 6.85747C6.93942 6.77502 6.98664 6.66379 6.9887 6.54721C6.99076 6.43063 6.94748 6.3178 6.86799 6.23249L4.84839 4.21289Z"
            fill="#232323"
            stroke="#232323"
            strokeWidth="0.5"
          />
        </svg>
      </div>
    </div>
  )
}
